// utils/linkHelper.js
import Link from 'next/link';
import { useRouter } from 'next/router';

import { prefetchRouteMedia } from '../../utils/mediaPrefetch';

const WP_BASE_URL = process.env.NEXT_PUBLIC_WORDPRESS_URL;

// Strip trailing slash, hash and query so two URLs that point at the same page
// compare equal (e.g. "/about/" === "/about" === "/about#team").
function normalizeForCompare(path) {
  if (!path) return '/';
  const withoutHashOrQuery = path.split('#')[0].split('?')[0];
  return withoutHashOrQuery.replace(/\/+$/, '') || '/';
}

/**
 * Returns a React element (<Link> or <a>) depending on whether the URL is internal or external
 */

function normalizeHref(href) {
  if (!href) return '';

  if (href.startsWith('/http://') || href.startsWith('/https://')) {
    href = href.slice(1);
  }

  // Media Library files live on WordPress, not on this frontend — point a
  // root-relative upload path back at the CMS so it's treated as external.
  if (WP_BASE_URL && href.startsWith('/wp-content/')) {
    href = WP_BASE_URL.replace(/\/+$/, '') + href;
  }

  return href;
}

export function SmartLink({
  href,
  children,
  onPointerEnter,
  onFocus,
  onTouchStart,
  ...props
}) {
  const router = useRouter();

  if (!href) return null;

  href = normalizeHref(href);

  const isWpUpload =
    WP_BASE_URL &&
    href.startsWith(WP_BASE_URL) &&
    href
      .slice(WP_BASE_URL.replace(/\/+$/, '').length)
      .startsWith('/wp-content/');

  const isInternal =
    !isWpUpload &&
    (href.startsWith('/') || (WP_BASE_URL && href.startsWith(WP_BASE_URL)));

  if (isInternal) {
    let relativeHref = href.replace(WP_BASE_URL, '');
    return (
      // <Link href={relativeHref} {...props}>
      <Link
        href={relativeHref}
        scroll={false}
        {...props}
        // next/link already prefetches the destination's JS and data. What it
        // can't know about is the HLS stream the homepage hero starts playing on
        // mount, whose playlists are only cached for 30s — so it has to be
        // warmed on intent, not ahead of time. No-op for every other href.
        // Handlers are composed rather than replaced so callers keep theirs.
        onPointerEnter={(e) => {
          onPointerEnter?.(e);
          prefetchRouteMedia(relativeHref);
        }}
        onFocus={(e) => {
          onFocus?.(e);
          prefetchRouteMedia(relativeHref);
        }}
        onTouchStart={(e) => {
          onTouchStart?.(e);
          prefetchRouteMedia(relativeHref);
        }}
        onNavigate={(e) => {
          // If the link points at the page we're already on, cancel the
          // navigation entirely — otherwise we'd stop Lenis without a page
          // transition to resume it, leaving scrolling paused.
          if (
            normalizeForCompare(relativeHref) ===
            normalizeForCompare(router.asPath)
          ) {
            e.preventDefault();
            // We've cancelled the navigation, so `routeChangeStart` will NOT
            // fire — anything that closes on route change (nav dropdowns, search)
            // needs a same-page signal instead. This is the counterpart event.
            window.dispatchEvent(new CustomEvent('samePageNav'));
            // Clicking the page you're already on should still feel responsive:
            // scroll back to the top. force:true so it works even while Lenis is
            // stopped (e.g. the mobile menu locks scroll while open).
            if (window.lenis) {
              window.lenis.scrollTo(0, { force: true });
            } else {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
            return;
          }
          // page transition stop scroll
          if (window.lenis) {
            window.lenis.stop();
          }
        }}
      >
        {children}
      </Link>
    );
  } else {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onPointerEnter={onPointerEnter}
        onFocus={onFocus}
        onTouchStart={onTouchStart}
        {...props}
      >
        {children}
      </a>
    );
  }
}
